import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  TooltipTrigger,
  Tooltip,
  ActionButton,
  Text
} from '@adobe/react-spectrum';
import {
  selectReleases,
  selectSearchFilterActive,
  selectSearchFilteredItems,
  selectHighlightedIssue,
  selectHighlightSource,
  selectMustWinView,
  selectMustWinItemIds
} from 'app/pages/Home/slice/selectors';
import { useHomeSlice } from 'app/pages/Home/slice';
import { MOMENT_TYPES, SEARCH_OUTSIDE_MUST_WINS_MSG } from 'utils/constants';
import { formatToMonthDayYear, formatQuarter, formatMonth } from 'utils/time';
import { DeleteConfirmationDialog } from 'app/components/shared/DeleteConfirmationDialog/DeleteConfirmationDialog';
import styles from './SearchField.module.scss';
import { clearViewStateFromUrl } from 'utils/viewStateDeepLink';
import { useMonthlySearchIndex } from 'app/components/MonthlyTimeline/hooks/useMonthlySearchIndex';
import { useRefreshSearchOnViewChange } from 'app/components/MonthlyTimeline/hooks/useRefreshSearchOnViewChange';
import { selectTimelineViewMode } from 'app/components/MonthlyTimeline/store/selectors';

/** Check if a search result (by id and optional phase) exists in Must Wins view */
function isResultInMustWins(
  result: { id: string; releasePhaseId?: string; showOnTimeline?: boolean },
  mustWinItemIds: Set<string>
): boolean {
  if (mustWinItemIds.has(result.id)) return true;
  if (result.releasePhaseId) {
    if (mustWinItemIds.has(`${result.id}-${result.releasePhaseId}`))
      return true;
    if (
      result.showOnTimeline !== undefined &&
      mustWinItemIds.has(
        `${result.id}-${result.releasePhaseId}-${result.showOnTimeline}`
      )
    )
      return true;
  }
  return false;
}

const SearchIcon = () => (
  <svg
    className={styles.searchIcon}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18.5303 17.4697L13.447 12.3865C14.4164 11.1851 15 9.66003 15 8C15 4.14062 11.8594 1 8 1C4.14062 1 1 4.14061 1 7.99999C1 11.8594 4.14062 15 8 15C9.66003 15 11.1851 14.4164 12.3865 13.447L17.4697 18.5303C17.6162 18.6767 17.8076 18.75 18 18.75C18.1924 18.75 18.3838 18.6768 18.5303 18.5303C18.8232 18.2373 18.8232 17.7627 18.5303 17.4697ZM8 13.5C4.96777 13.5 2.5 11.0322 2.5 7.99999C2.5 4.96776 4.96777 2.49999 8 2.49999C11.0322 2.49999 13.5 4.96776 13.5 7.99999C13.5 11.0322 11.0322 13.5 8 13.5Z"
      fill="#292929"
    />
  </svg>
);

const ClearIcon = () => (
  <svg
    className={styles.clearIcon}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM5.5 5.5C5.77614 5.22386 6.22386 5.22386 6.5 5.5L8 7L9.5 5.5C9.77614 5.22386 10.2239 5.22386 10.5 5.5C10.7761 5.77614 10.7761 6.22386 10.5 6.5L9 8L10.5 9.5C10.7761 9.77614 10.7761 10.2239 10.5 10.5C10.2239 10.7761 9.77614 10.7761 9.5 10.5L8 9L6.5 10.5C6.22386 10.7761 5.77614 10.7761 5.5 10.5C5.22386 10.2239 5.22386 9.77614 5.5 9.5L7 8L5.5 6.5C5.22386 6.22386 5.22386 5.77614 5.5 5.5Z"
      fill="#757575"
    />
  </svg>
);

interface SearchResult {
  id: string;
  title: string;
  type: 'main' | 'child';
  parentId?: string;
  parentTitle?: string;
  productGroups?: string;
  categories?: string[];
  momentType?: string;
  isInTheWorks?: boolean;
  releasePhase?: string;
  releasePhaseId?: string;
  showOnTimeline?: boolean;
  tier?: string; // Add tier information
  targetDate?: string; // Add target date for moments
  gaDate?: string; // Add GA date for features
}

interface SearchFieldProps {
  value?: string;
  onChange?:
    | ((e: React.ChangeEvent<HTMLInputElement>) => void)
    | ((value: string) => void);
  placeholder?: string;
  showDropdown?: boolean;
  onResultSelect?: (result: SearchResult) => void;
  searchResults?: SearchResult[];
  onEnterPress?: () => void;
  onInputClick?: () => void;
  onClear?: () => void;
  searchFilterActive?: boolean;
  searchFilteredItemsCount?: number;
  [key: string]: any;
}

function getSearchResultSelectionKey(result: SearchResult): string {
  return result.releasePhaseId
    ? `${result.id}-${result.parentId || 'noparent'}-${result.releasePhaseId}-${
        result.showOnTimeline
      }`
    : result.parentId
    ? `${result.id}-${result.parentId}`
    : result.id;
}

function findSearchResultBySelectionKey(
  selectedKey: string,
  results: SearchResult[],
  fallbackResults: SearchResult[]
): SearchResult | undefined {
  return (
    results.find(r => getSearchResultSelectionKey(r) === selectedKey) ||
    fallbackResults.find(r => getSearchResultSelectionKey(r) === selectedKey)
  );
}

// TimelineCard expects id-phaseId-showOnTimeline; filtering uses id-phaseId only
function buildHighlightKeysForResult(result: SearchResult): string[] {
  if (result.type === 'child' || !result.releasePhaseId) {
    return result.id ? [result.id] : [];
  }

  const showOnTimeline = result.showOnTimeline ?? true;
  const keys = [`${result.id}-${result.releasePhaseId}-${showOnTimeline}`];

  if (result.releasePhase) {
    keys.push(`${result.id}-${result.releasePhase}-${showOnTimeline}`);
  }

  return keys;
}

// Helper function to format dates for display in search pills (matches timeline format)
const formatDateForPill = (dateStr: string | undefined): string => {
  if (!dateStr) return '';

  try {
    // Handle quarter format (Q1 2025 → Q1, 2025)
    if (dateStr.match(/^Q[1-4]\s+\d{4}$/i)) {
      return formatQuarter(dateStr);
    }

    // Handle month format (Feb 2025 → February, 2025)
    if (dateStr.match(/^[A-Za-z]+\s+\d{4}$/)) {
      return formatMonth(dateStr);
    }

    // Handle specific date format (2025-02-15 → February 15, 2025)
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return formatToMonthDayYear(dateStr);
    }

    // Return as-is if no pattern matches
    return dateStr;
  } catch (error) {
    console.error('Error formatting date for pill:', error, dateStr);
    return dateStr || '';
  }
};

const SearchField = ({
  value = '',
  onChange,
  placeholder = 'Search…',
  showDropdown = false,
  onResultSelect,
  searchResults = [],
  onEnterPress,
  onInputClick,
  onClear,
  searchFilterActive = false,
  searchFilteredItemsCount = 0,
  ...props
}: SearchFieldProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsListRef = useRef<HTMLDivElement>(null);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchResults]);

  // Shared tooltip state for all tooltips in the dropdown
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleTooltipMouseEnter = (key: string) => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    tooltipTimerRef.current = setTimeout(() => {
      setOpenTooltip(key);
    }, 1000);
  };

  const handleTooltipMouseLeave = () => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    setOpenTooltip(null);
  };

  useEffect(() => {
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
    };
  }, []);

  // Function to check if text is truncated (has ellipsis)
  const isTextTruncated = (element: HTMLElement | null): boolean => {
    if (!element) return false;
    return element.scrollWidth > element.clientWidth;
  };

  // Always call onChange with e.target.value (string), since that's the expected usage in SearchBar
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onChange) return;
    // If the function expects a string, call with value
    (onChange as (value: string) => void)(e.target.value);
  };

  const handleClear = () => {
    if (onChange) {
      (onChange as (value: string) => void)('');
    }
    if (onClear) {
      onClear();
    }
  };

  const handleResultClick = (result: SearchResult) => {
    if (onResultSelect) {
      onResultSelect(result);
    }

    // Don't auto-apply enter - let user select multiple items and press Enter when ready
    // The selection will trigger immediate highlighting/filtering through handleResultSelect

    // Keep focus on input field so Enter key continues to work
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && showDropdown && searchResults.length > 0) {
      e.preventDefault();
      setHighlightedIndex(prev => {
        const next = Math.min(prev + 1, searchResults.length - 1);
        requestAnimationFrame(() => {
          resultsListRef.current
            ?.querySelector(`[data-result-index="${next}"]`)
            ?.scrollIntoView({ block: 'nearest' });
        });
        return next;
      });
    } else if (
      e.key === 'ArrowUp' &&
      showDropdown &&
      searchResults.length > 0
    ) {
      e.preventDefault();
      setHighlightedIndex(prev => {
        const next = Math.max(prev - 1, 0);
        requestAnimationFrame(() => {
          resultsListRef.current
            ?.querySelector(`[data-result-index="${next}"]`)
            ?.scrollIntoView({ block: 'nearest' });
        });
        return next;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < searchResults.length) {
        handleResultClick(searchResults[highlightedIndex]);
      } else if (onEnterPress) {
        onEnterPress();
      }
    } else if (e.key === 'Escape' && showDropdown) {
      setHighlightedIndex(-1);
    }
  };

  const handleInputClick = () => {
    if (onInputClick) {
      onInputClick();
    }
  };

  // Component to render title with conditional tooltip
  const TitleWithTooltip = React.memo(
    ({
      title,
      className,
      resultId
    }: {
      title: string;
      className: string;
      resultId: string;
    }) => {
      const titleRef = useRef<HTMLDivElement>(null);
      const [showTooltip, setShowTooltip] = useState(false);
      const tooltipKey = `title-${resultId}`;

      useEffect(() => {
        // Check if text is truncated after component mounts
        const checkTruncation = () => {
          if (titleRef.current) {
            const isTruncated = isTextTruncated(titleRef.current);
            setShowTooltip(isTruncated);
          }
        };

        // Check on mount
        checkTruncation();

        // Also check after a small delay to ensure styles are applied
        const timer = setTimeout(checkTruncation, 100);
        return () => clearTimeout(timer);
      }, [title]); // Only re-run when title changes

      // Always render the same structure to prevent layout shifts
      const content = (
        <div ref={titleRef} className={className}>
          {title}
        </div>
      );

      if (showTooltip) {
        return (
          <TooltipTrigger
            isOpen={openTooltip === tooltipKey}
            onOpenChange={isOpen => {
              if (!isOpen) setOpenTooltip(null);
            }}
            placement="top"
            offset={6}
          >
            <div
              onMouseEnter={() => handleTooltipMouseEnter(tooltipKey)}
              onMouseLeave={handleTooltipMouseLeave}
              style={{ display: 'contents' }}
            >
              <ActionButton isQuiet onPress={() => {}}>
                {content}
              </ActionButton>
            </div>
            <Tooltip>{title}</Tooltip>
          </TooltipTrigger>
        );
      }

      return content;
    }
  );

  // Component to render parent title with conditional tooltip
  const ParentTitleWithTooltip = React.memo(
    ({ parentTitle, resultId }: { parentTitle: string; resultId: string }) => {
      const parentRef = useRef<HTMLDivElement>(null);
      const [showTooltip, setShowTooltip] = useState(false);
      const fullParentText = `Parent: ${parentTitle}`;
      const tooltipKey = `parent-${resultId}`;

      useEffect(() => {
        // Check if text is truncated after component mounts
        const checkTruncation = () => {
          if (parentRef.current) {
            const isTruncated = isTextTruncated(parentRef.current);
            setShowTooltip(isTruncated);
          }
        };

        // Check on mount
        checkTruncation();

        // Also check after a small delay to ensure styles are applied
        const timer = setTimeout(checkTruncation, 100);
        return () => clearTimeout(timer);
      }, [parentTitle]); // Only re-run when parentTitle changes

      // Always render the same structure to prevent layout shifts
      const content = (
        <div ref={parentRef} className={styles.resultParent}>
          {fullParentText}
        </div>
      );

      if (showTooltip) {
        return (
          <TooltipTrigger
            isOpen={openTooltip === tooltipKey}
            onOpenChange={isOpen => {
              if (!isOpen) setOpenTooltip(null);
            }}
            placement="top"
            offset={6}
          >
            <div
              onMouseEnter={() => handleTooltipMouseEnter(tooltipKey)}
              onMouseLeave={handleTooltipMouseLeave}
              style={{ display: 'contents' }}
            >
              <ActionButton isQuiet onPress={() => {}}>
                {content}
              </ActionButton>
            </div>
            <Tooltip>{fullParentText}</Tooltip>
          </TooltipTrigger>
        );
      }

      return content;
    }
  );

  return (
    <div
      className={`${styles.searchContainer} ${
        searchFilterActive ? styles.searchFilterActive : ''
      }`}
      ref={containerRef}
    >
      <span className={styles.searchIcon}>
        <SearchIcon />
      </span>
      <input
        className={styles.searchInput}
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={handleInputClick}
        placeholder={
          searchFilterActive && !value
            ? `${searchFilteredItemsCount} item${
                searchFilteredItemsCount !== 1 ? 's' : ''
              } filtered`
            : placeholder
        }
        ref={inputRef}
        {...props}
      />
      {(value || searchFilterActive) && (
        <button
          className={styles.clearButton}
          onClick={handleClear}
          type="button"
          aria-label="Clear search"
        >
          <ClearIcon />
        </button>
      )}

      {/* Search Results Dropdown */}
      {showDropdown && (
        <div className={styles.searchDropdown}>
          <div className={styles.searchResultsHeader}>
            <span className={styles.resultsCount}>
              {searchResults.length === 0
                ? 'No matching results found.'
                : `${searchResults.length} result${
                    searchResults.length !== 1 ? 's' : ''
                  } found`}
            </span>
          </div>
          {searchResults.length > 0 && (
            <div ref={resultsListRef} className={styles.searchResultsList}>
              {(() => {
                return searchResults.map((result, index) => {
                  // Create composite key including phase information
                  const compositeKey = result.releasePhaseId
                    ? `${result.id}-${result.parentId || 'noparent'}-${
                        result.releasePhaseId
                      }-${result.showOnTimeline}`
                    : result.parentId
                    ? `${result.id}-${result.parentId}`
                    : result.id;

                  return (
                    <div
                      key={compositeKey}
                      data-result-index={index}
                      className={`${styles.searchResultItem} ${
                        result.type === 'child'
                          ? styles.childResult
                          : styles.mainResult
                      } ${(result as any).selected ? styles.selected : ''}`}
                      style={
                        highlightedIndex === index
                          ? { backgroundColor: '#e9e9e9', borderRadius: '8px' }
                          : undefined
                      }
                      onClick={() => handleResultClick(result)}
                    >
                      <div className={styles.resultContent}>
                        <TitleWithTooltip
                          title={result.title}
                          className={styles.resultTitle}
                          resultId={result.id}
                        />
                        {result.type === 'child' && result.parentTitle && (
                          <ParentTitleWithTooltip
                            parentTitle={result.parentTitle}
                            resultId={result.id}
                          />
                        )}
                        <div className={styles.resultMeta}>
                          {/* ✅ Don't show tier/date for Features moment type */}
                          {result.tier &&
                            result.momentType !== MOMENT_TYPES.FEATURE && (
                              <span className={styles.tierTag}>
                                {result.tier}
                              </span>
                            )}
                          {result.targetDate &&
                            result.momentType !== MOMENT_TYPES.FEATURE && (
                              <span className={styles.targetDateTag}>
                                {formatDateForPill(result.targetDate)}
                              </span>
                            )}
                          {result.gaDate &&
                            result.momentType !== MOMENT_TYPES.FEATURE && (
                              <span className={styles.gaDateTag}>
                                {formatDateForPill(result.gaDate)}
                              </span>
                            )}
                          {result.releasePhase &&
                            result.momentType !== MOMENT_TYPES.FEATURE && (
                              <span className={styles.releasePhaseTag}>
                                {result.releasePhase}
                              </span>
                            )}
                          {result.isInTheWorks && (
                            <span className={styles.inTheWorksTag}>
                              In the Works
                            </span>
                          )}
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          flexShrink: 0,
                          pointerEvents: 'none' // Allow clicks to pass through to parent
                        }}
                      >
                        <div className={styles.resultType}>
                          {result.momentType || MOMENT_TYPES.PRODUCT_RELEASE}
                        </div>
                        {(result as any).selected && (
                          <span className={styles.selectedIndicator}>✓</span>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SearchBar = () => {
  const [searchValue, setSearchValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [selectedResults, setSelectedResults] = useState<Set<string>>(
    new Set()
  );
  const [lastSearchResults, setLastSearchResults] = useState<SearchResult[]>(
    []
  );
  const [dropdownHiddenByEnter, setDropdownHiddenByEnter] = useState(false);
  const [searchOutsideDialogOpen, setSearchOutsideDialogOpen] = useState(false);
  const pendingSearchApplyRef = useRef<(() => void) | null>(null);
  /** Composite keys of the result(s) that triggered the dialog (outside Must Wins); removed from selection on Cancel */
  const pendingCancelRemoveKeysRef = useRef<Set<string> | null>(null);
  const releases = useSelector(selectReleases);
  const { actions } = useHomeSlice();
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const searchFilterActive = useSelector(selectSearchFilterActive);
  const searchFilteredItems = useSelector(selectSearchFilteredItems);
  const highlightedIssue = useSelector(selectHighlightedIssue); // ✅ Track current highlights
  const highlightSource = useSelector(selectHighlightSource); // ✅ Track highlight source
  const mustWinViewActive = useSelector(selectMustWinView);
  const mustWinItemIds = useSelector(selectMustWinItemIds);
  const timelineViewMode = useSelector(selectTimelineViewMode);
  const monthlySearchIndex = useMonthlySearchIndex();
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Memoize TBD item IDs to avoid recalculating on every search
  // NOTE: Relies on Redux properly updating the 'releases' object reference when data changes
  // If Redux uses immutable updates (as it should), any change to data will trigger recalculation
  // ✅ FIX: Track issueId+releasePhaseId combos, not just issueId
  // This prevents skipping all phases when only one phase is in TBD
  const tbdItemIds = useMemo(() => {
    const ids = new Set<string>();
    if (releases?.tbd && Array.isArray(releases.tbd)) {
      releases.tbd.forEach((tbdSection: any) => {
        if (tbdSection.project && Array.isArray(tbdSection.project)) {
          tbdSection.project.forEach((tbdItem: any) => {
            if (tbdItem.issueId) {
              // Create composite key with phase information
              const compositeKey = tbdItem.releasePhaseId
                ? `${tbdItem.issueId}-${tbdItem.releasePhaseId}`
                : tbdItem.issueId;
              ids.add(compositeKey);
            }
            if (
              tbdItem.childRelationships &&
              tbdItem.childRelationships.length > 0
            ) {
              tbdItem.childRelationships.forEach((child: any) => {
                if (child.issueId) {
                  // Children typically don't have phases, use just ID
                  ids.add(child.issueId);
                }
              });
            }
            if (tbdItem.features && tbdItem.features.length > 0) {
              tbdItem.features.forEach((feature: any) => {
                if (feature.featureId || feature.issueId) {
                  // Features typically don't have phases, use just ID
                  ids.add(feature.featureId || feature.issueId);
                }
              });
            }
          });
        }
      });
    }
    return ids;
  }, [releases]);

  // Memoize search index - build once when releases data changes
  // This dramatically improves search performance by pre-processing all searchable items
  const quarterlySearchIndex = useMemo(() => {
    if (!releases?.releaseScheduled) {
      return { items: [], wordIndex: new Map<string, number[]>() };
    }

    const allSearchableItems: Array<{
      item: SearchResult;
      searchText: string;
    }> = [];

    // Helper function to get title from either title or name property
    const getTitle = (item: any) => item.title || item.name || '';

    // Helper function to normalize text for searching
    // Removes invisible characters (zero-width spaces, etc.) and normalizes whitespace
    // Keeps special characters like : and - as they may be part of product names
    const normalizeSearchText = (text: string): string => {
      return (
        text
          .toLowerCase()
          // Remove zero-width spaces, zero-width joiners, and other invisible Unicode characters
          .replace(/[\u200B-\u200D\uFEFF]/g, '')
          // Keep alphanumeric, spaces, hyphens, and colons (common in product names)
          // Remove other punctuation (parentheses, quotes, etc.)
          // This allows searching "ffe:" or "ffe-3p" as they appear in names
          .replace(/[^\w\s:\-]/g, ' ')
          // Replace multiple spaces with single space
          .replace(/\s+/g, ' ')
          // Trim leading/trailing spaces
          .trim()
      );
    };

    // Index releaseScheduled data (excluding TBD items)
    releases.releaseScheduled.forEach(release => {
      release.projects.forEach(project => {
        project.project.forEach(subProject => {
          // ✅ FIX: Check composite key (issueId + releasePhaseId) to skip only the exact phase that's in TBD
          // This allows other phases of the same ARM ID to be indexed
          const compositeKey = subProject.releasePhaseId
            ? `${subProject.issueId}-${subProject.releasePhaseId}`
            : subProject.issueId;

          if (tbdItemIds.has(compositeKey)) {
            return;
          }

          const projectTitle = getTitle(subProject);

          // Index main project
          if (projectTitle) {
            // ✅ Determine if this item should be shown in "In the Works" section
            // Items with any date status: "TBD" are displayed in "In the Works"
            const isInTheWorks = !!(
              subProject.dates?.targetDateStatus === 'TBD' ||
              subProject.dates?.releaseDateStatus === 'TBD' ||
              subProject.dates?.effectiveDateStatus === 'TBD'
            );

            // Extract target date for moments with fallback to releaseDate if no target date exists
            // Some moment types (e.g., "Product Release without Marketing") only have releaseDate
            const targetDate =
              subProject.dates?.targetStart ||
              subProject.dates?.targetMonth ||
              subProject.dates?.targetQuarter ||
              subProject.dates?.releaseDate || // Fallback for moment types without targetStart
              subProject.dates?.releaseMonth || // Fallback for month-based releases
              subProject.dates?.releaseQuarter || // Fallback for quarter-based releases
              subProject.dates?.effectiveDate || // Fallback for year-based releases
              subProject.dates?.effectiveMonth || // Fallback for year-based releases
              subProject.dates?.effectiveQuarter || // Fallback for year-based releases
              undefined;

            allSearchableItems.push({
              item: {
                id: subProject.issueId,
                title: projectTitle,
                type: 'main',
                productGroups: project.productGroups,
                categories: project.categories,
                momentType:
                  subProject.momentType ||
                  subProject.projectType ||
                  MOMENT_TYPES.PRODUCT_RELEASE,
                releasePhase: subProject.releasePhase,
                releasePhaseId: subProject.releasePhaseId,
                showOnTimeline: subProject.showOnTimeline,
                isInTheWorks: isInTheWorks, // ✅ Mark items with TBD status (explicitly boolean)
                tier: subProject.priority || subProject.tier, // Use priority (mapped from tier in API)
                targetDate: targetDate // Add target date for moments
              },
              searchText: normalizeSearchText(projectTitle)
            });
          }

          // Index child relationships
          if (
            subProject.childRelationships &&
            subProject.childRelationships.length > 0
          ) {
            subProject.childRelationships.forEach(child => {
              // Children typically don't have phases, check just the ID
              if (tbdItemIds.has(child.issueId)) {
                return;
              }

              const childTitle = getTitle(child);
              if (childTitle) {
                // Extract GA date for features (effective date)
                const gaDate =
                  child.dates?.effectiveDate ||
                  child.dates?.effectiveMonth ||
                  child.dates?.effectiveQuarter ||
                  undefined;

                allSearchableItems.push({
                  item: {
                    id: child.issueId,
                    title: childTitle,
                    type: 'child',
                    parentId: subProject.issueId,
                    parentTitle: projectTitle,
                    productGroups: project.productGroups,
                    categories: project.categories,
                    momentType:
                      child.momentType ||
                      child.projectType ||
                      MOMENT_TYPES.PRODUCT_RELEASE,
                    tier:
                      child.priority ||
                      child.tier ||
                      subProject.priority ||
                      subProject.tier, // Use priority (mapped from tier in API)
                    gaDate: gaDate // Add GA date for features
                  },
                  searchText: normalizeSearchText(childTitle)
                });
              }

              // Index nested child relationships
              if (
                child.childRelationships &&
                child.childRelationships.length > 0
              ) {
                child.childRelationships.forEach(grandChild => {
                  // Grandchildren typically don't have phases, check just the ID
                  if (tbdItemIds.has(grandChild.issueId)) {
                    return;
                  }

                  const grandChildTitle = getTitle(grandChild);
                  if (grandChildTitle) {
                    allSearchableItems.push({
                      item: {
                        id: grandChild.issueId,
                        title: grandChildTitle,
                        type: 'child',
                        parentId: child.issueId,
                        parentTitle: childTitle,
                        productGroups: project.productGroups,
                        categories: project.categories,
                        momentType:
                          grandChild.momentType ||
                          grandChild.projectType ||
                          MOMENT_TYPES.FEATURE,
                        tier:
                          grandChild.priority ||
                          grandChild.tier ||
                          child.priority ||
                          child.tier ||
                          subProject.priority ||
                          subProject.tier // Use priority (mapped from tier in API)
                      },
                      searchText: normalizeSearchText(grandChildTitle)
                    });
                  }
                });
              }
            });
          }

          // Index features array
          if (subProject.features && subProject.features.length > 0) {
            subProject.features.forEach(feature => {
              const featureUniqueId = feature.featureId || feature.issueId;

              // Features typically don't have phases, check just the ID
              if (tbdItemIds.has(featureUniqueId)) {
                return;
              }

              const featureTitle = getTitle(feature);
              if (featureTitle) {
                // Extract GA date for features (effective date)
                const gaDate =
                  feature.dates?.effectiveDate ||
                  feature.dates?.effectiveMonth ||
                  feature.dates?.effectiveQuarter ||
                  undefined;

                allSearchableItems.push({
                  item: {
                    id: featureUniqueId,
                    title: featureTitle,
                    type: 'child',
                    parentId: subProject.issueId,
                    parentTitle: projectTitle,
                    productGroups: project.productGroups,
                    categories: project.categories,
                    momentType: MOMENT_TYPES.FEATURE,
                    releasePhase: subProject.releasePhase,
                    releasePhaseId: subProject.releasePhaseId,
                    showOnTimeline: subProject.showOnTimeline,
                    tier: subProject.priority || subProject.tier, // Use priority (mapped from tier in API)
                    gaDate: gaDate // Add GA date for features
                  },
                  searchText: normalizeSearchText(featureTitle)
                });
              }
            });
          }
        });
      });
    });

    // Index "in the works" (tbd) data
    if (releases.tbd && Array.isArray(releases.tbd)) {
      releases.tbd.forEach((tbdSection: any) => {
        if (tbdSection.project && Array.isArray(tbdSection.project)) {
          tbdSection.project.forEach((tbdItem: any) => {
            const tbdTitle = getTitle(tbdItem);

            if (tbdTitle) {
              // Extract target date for TBD moments with fallback to releaseDate if no target date exists
              // Some moment types (e.g., "Product Release without Marketing") only have releaseDate
              const targetDate =
                tbdItem.dates?.targetStart ||
                tbdItem.dates?.targetMonth ||
                tbdItem.dates?.targetQuarter ||
                tbdItem.dates?.releaseDate || // Fallback for moment types without targetStart
                tbdItem.dates?.releaseMonth || // Fallback for month-based releases
                tbdItem.dates?.releaseQuarter || // Fallback for quarter-based releases
                tbdItem.dates?.effectiveDate || // Fallback for year-based releases
                tbdItem.dates?.effectiveMonth || // Fallback for year-based releases
                tbdItem.dates?.effectiveQuarter || // Fallback for year-based releases
                undefined;

              allSearchableItems.push({
                item: {
                  id: tbdItem.issueId,
                  title: tbdTitle,
                  type: 'main',
                  productGroups:
                    tbdItem.productGroups || tbdSection.productGroups,
                  categories: tbdItem.categories || tbdSection.categories,
                  momentType:
                    tbdItem.momentType ||
                    tbdItem.projectType ||
                    MOMENT_TYPES.PRODUCT_RELEASE,
                  isInTheWorks: true,
                  releasePhase: tbdItem.releasePhase, // ✅ Include releasePhase for composite key matching
                  releasePhaseId: tbdItem.releasePhaseId, // ✅ Include releasePhaseId for composite key matching
                  showOnTimeline: tbdItem.showOnTimeline || false, // ✅ Include showOnTimeline
                  tier: tbdItem.priority || tbdItem.tier, // Use priority (mapped from tier in API)
                  targetDate: targetDate // Add target date for TBD moments
                },
                searchText: normalizeSearchText(tbdTitle)
              });
            }

            // Index child relationships in tbd items
            if (
              tbdItem.childRelationships &&
              tbdItem.childRelationships.length > 0
            ) {
              tbdItem.childRelationships.forEach((child: any) => {
                const childTitle = getTitle(child);
                if (childTitle) {
                  // Extract GA date for child relationships (effective date)
                  const gaDate =
                    child.dates?.effectiveDate ||
                    child.dates?.effectiveMonth ||
                    child.dates?.effectiveQuarter ||
                    undefined;

                  allSearchableItems.push({
                    item: {
                      id: child.issueId,
                      title: childTitle,
                      type: 'child',
                      parentId: tbdItem.issueId,
                      parentTitle: tbdTitle,
                      productGroups:
                        tbdItem.productGroups || tbdSection.productGroups,
                      categories: tbdItem.categories || tbdSection.categories,
                      momentType:
                        child.momentType ||
                        child.projectType ||
                        MOMENT_TYPES.PRODUCT_RELEASE,
                      isInTheWorks: true,
                      tier:
                        child.priority ||
                        child.tier ||
                        tbdItem.priority ||
                        tbdItem.tier, // Use priority (mapped from tier in API)
                      gaDate: gaDate // Add GA date for child relationships
                    },
                    searchText: normalizeSearchText(childTitle)
                  });
                }
              });
            }

            // Index features in tbd items
            if (tbdItem.features && tbdItem.features.length > 0) {
              tbdItem.features.forEach((feature: any) => {
                const featureUniqueId = feature.featureId || feature.issueId;
                const featureTitle = getTitle(feature);
                if (featureTitle) {
                  // Extract GA date for features (effective date)
                  const gaDate =
                    feature.dates?.effectiveDate ||
                    feature.dates?.effectiveMonth ||
                    feature.dates?.effectiveQuarter ||
                    undefined;

                  allSearchableItems.push({
                    item: {
                      id: featureUniqueId,
                      title: featureTitle,
                      type: 'child',
                      parentId: tbdItem.issueId,
                      parentTitle: tbdTitle,
                      productGroups:
                        tbdItem.productGroups || tbdSection.productGroups,
                      categories: tbdItem.categories || tbdSection.categories,
                      momentType: MOMENT_TYPES.FEATURE,
                      isInTheWorks: true,
                      tier: tbdItem.priority || tbdItem.tier, // Use priority (mapped from tier in API)
                      gaDate: gaDate // Add GA date for features
                    },
                    searchText: normalizeSearchText(featureTitle)
                  });
                }
              });
            }
          });
        }
      });
    }

    // Build inverted index: Map each word to array of item indices
    // This enables O(1) word lookup instead of O(n) string scanning
    const wordIndex = new Map<string, number[]>();

    // searchWords is attached in the same pass, so the per-item split happens once here at index
    // build time instead of on every keystroke inside generateSearchResults.
    const indexedItems = allSearchableItems.map((entry, index) => {
      // Split search text into words and create index entries
      const words = entry.searchText.split(/\s+/);
      words.forEach(word => {
        if (word.length >= 2) {
          // Only index words with 2+ characters
          if (!wordIndex.has(word)) {
            wordIndex.set(word, []);
          }
          wordIndex.get(word)!.push(index);
        }
      });
      return { ...entry, searchWords: words };
    });

    return { items: indexedItems, wordIndex };
  }, [releases, tbdItemIds]);

  const searchIndex =
    timelineViewMode === 'monthly' ? monthlySearchIndex : quarterlySearchIndex;

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Close dropdown when clicking outside or on other components
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Close if clicking outside the search container
      if (containerRef.current && !containerRef.current.contains(target)) {
        setShowDropdown(false);
        return;
      }

      // Close if clicking on specific components (calendar, filters, etc.)
      const shouldCloseSelectors = [
        '[data-testid*="calendar"]',
        '[data-testid*="filter"]',
        '[data-testid*="dropdown"]',
        '[data-testid*="menu"]',
        '[class*="spectrum-Picker"]',
        '[class*="spectrum-Menu"]',
        '[class*="spectrum-Calendar"]',
        '[class*="spectrum-ActionButton"]',
        '[role="button"]',
        '[role="combobox"]',
        '[role="listbox"]',
        'button',
        'select'
      ];

      const shouldClose = shouldCloseSelectors.some(selector => {
        try {
          return target.closest(selector) !== null;
        } catch (e) {
          return false;
        }
      });

      if (shouldClose) {
        setShowDropdown(false);
      }
    };

    const handleFocusChange = (event: FocusEvent) => {
      const target = event.target as HTMLElement;

      // Close dropdown if focus moves to other interactive elements
      if (containerRef.current && !containerRef.current.contains(target)) {
        // Check if the focused element is an interactive component
        const interactiveElements = [
          'input',
          'button',
          'select',
          'textarea',
          '[role="button"]',
          '[role="combobox"]',
          '[role="listbox"]',
          '[tabindex]'
        ];

        const isInteractive = interactiveElements.some(selector => {
          try {
            return target.matches(selector) || target.closest(selector);
          } catch (e) {
            return false;
          }
        });

        if (isInteractive) {
          setShowDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('focusin', handleFocusChange);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('focusin', handleFocusChange);
    };
  }, []);

  const generateSearchResults = useCallback(
    (value: string): SearchResult[] => {
      if (!searchIndex || !searchIndex.items.length || value.length < 3) {
        return [];
      }

      // Normalize the search term to match how we indexed the data
      // This removes invisible characters and normalizes whitespace
      // Keeps special characters like : and - for product name searches
      const searchTerm = value
        .toLowerCase()
        .replace(/[\u200B-\u200D\uFEFF]/g, '') // Remove zero-width spaces
        .replace(/[^\w\s:\-]/g, ' ') // Keep alphanumeric, spaces, colons, hyphens
        .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
        .trim();

      const MAX_RESULTS = 50; // Limit results for better performance and UX

      // Use word boundary search - match only at word boundaries, not inside words
      // This ensures "ffe" matches "FFE 3P" but NOT "offer"
      const matchedIndices = new Set<number>();

      searchIndex.items.forEach((entry, index) => {
        // Words are pre-split at index build time. Splitting here re-ran a regex over every
        // item's search text on every keystroke, which is the bulk of per-keystroke search cost.
        const words = entry.searchWords;

        // Check if any word starts with the search term (word boundary match)
        const matchesWordBoundary = words.some(word =>
          word.startsWith(searchTerm)
        );

        // Also check for exact phrase match if search term contains spaces
        const matchesPhrase = entry.searchText.includes(searchTerm);

        // Include result if it matches word boundary OR is an exact phrase match
        if (
          matchesWordBoundary ||
          (searchTerm.includes(' ') && matchesPhrase)
        ) {
          matchedIndices.add(index);
        }
      });

      // Convert matched indices to results
      const results = Array.from(matchedIndices)
        .slice(0, MAX_RESULTS * 2) // Get more than needed before deduplication
        .map(index => searchIndex.items[index].item);

      // ✅ SIMPLIFIED DEDUPLICATION LOGIC
      // The goal: Show ALL unique phases, but deduplicate mirror cards (same phase, different showOnTimeline)
      // Key insight:
      // - Different phases (different releasePhaseId) = DIFFERENT suggestions (user wants to see both)
      // - Same phase with showOnTimeline true/false = DUPLICATE (only show the timeline version)
      const uniqueResults: SearchResult[] = [];
      const seenKeys = new Map<string, SearchResult>();

      results.forEach(result => {
        if (result.type === 'child') {
          // For features/children, only keep first instance based on ID
          if (!seenKeys.has(result.id)) {
            seenKeys.set(result.id, result);
            uniqueResults.push(result);
          }
        } else {
          // For parent/main cards:
          // Create a unique key based ONLY on ID + releasePhaseId
          // This means: Different phases = different suggestions (✓ correct)
          // Same phase in TBD and Release Scheduled = different suggestions (✓ user wants this)
          const uniqueKey = result.releasePhaseId
            ? `${result.id}-${result.releasePhaseId}`
            : result.id;

          const existingResult = seenKeys.get(uniqueKey);

          if (!existingResult) {
            // First time seeing this ID+Phase combo - add it
            seenKeys.set(uniqueKey, result);
            uniqueResults.push(result);
          } else {
            // We've seen this ID+Phase combo before - decide which to keep
            // This handles mirror cards (same phase, different showOnTimeline)
            let shouldReplace = false;

            // Priority 1: Prefer timeline cards (showOnTimeline=true)
            if (result.showOnTimeline && !existingResult.showOnTimeline) {
              shouldReplace = true;
            }
            // Priority 2: If both have same showOnTimeline, prefer scheduled over TBD
            else if (
              result.showOnTimeline === existingResult.showOnTimeline &&
              !result.isInTheWorks &&
              existingResult.isInTheWorks
            ) {
              shouldReplace = true;
            }

            if (shouldReplace) {
              // Replace the existing result with the higher-priority one
              const existingIndex = uniqueResults.findIndex(
                r =>
                  (r.releasePhaseId ? `${r.id}-${r.releasePhaseId}` : r.id) ===
                  uniqueKey
              );
              if (existingIndex !== -1) {
                uniqueResults[existingIndex] = result;
              }
              seenKeys.set(uniqueKey, result);
            }
          }
        }
      });

      // Limit final results for better performance
      const finalResults = uniqueResults.slice(0, MAX_RESULTS);

      return finalResults;
    },
    [searchIndex]
  );

  const findAllParentIds = useCallback(
    (childId: string): string[] => {
      const parentIds: string[] = [];

      if (!releases?.releaseScheduled) return parentIds;

      // Search in releaseScheduled data
      releases.releaseScheduled.forEach((releaseType: any) => {
        releaseType.projects?.forEach((quarterProject: any) => {
          quarterProject.project?.forEach((project: any) => {
            // Create composite key using issueId, releasePhaseId, and showOnTimeline
            const compositeKey = project.releasePhaseId
              ? `${project.issueId}-${project.releasePhaseId}-${project.showOnTimeline}`
              : project.issueId;

            if (project.childRelationships?.length > 0) {
              project.childRelationships.forEach((child: any) => {
                if (child.issueId === childId) {
                  parentIds.push(compositeKey);
                }

                // Check nested children (grandchildren)
                if (child.childRelationships?.length > 0) {
                  child.childRelationships.forEach((grandChild: any) => {
                    if (grandChild.issueId === childId) {
                      parentIds.push(compositeKey);
                      const childCompositeKey = child.releasePhaseId
                        ? `${child.issueId}-${child.releasePhaseId}-${child.showOnTimeline}`
                        : child.issueId;
                      parentIds.push(childCompositeKey);
                    }
                  });
                }
              });
            }

            // Check features array
            if (project.features?.length > 0) {
              project.features.forEach((feature: any) => {
                const featureUniqueId = feature.featureId || feature.issueId;
                if (featureUniqueId === childId) {
                  parentIds.push(compositeKey);
                }
              });
            }
          });
        });
      });

      // Search in tbd data
      if (releases.tbd && Array.isArray(releases.tbd)) {
        releases.tbd.forEach((tbdSection: any) => {
          if (tbdSection.project && Array.isArray(tbdSection.project)) {
            tbdSection.project.forEach((tbdItem: any) => {
              // Create composite key using issueId, releasePhaseId, and showOnTimeline
              const compositeKey = tbdItem.releasePhaseId
                ? `${tbdItem.issueId}-${tbdItem.releasePhaseId}-${tbdItem.showOnTimeline}`
                : tbdItem.issueId;

              if (tbdItem.childRelationships?.length > 0) {
                tbdItem.childRelationships.forEach((child: any) => {
                  if (child.issueId === childId) {
                    parentIds.push(compositeKey);
                  }
                });
              }

              // Check features in tbd items
              if (tbdItem.features?.length > 0) {
                tbdItem.features.forEach((feature: any) => {
                  const featureUniqueId = feature.featureId || feature.issueId;
                  if (featureUniqueId === childId) {
                    parentIds.push(compositeKey);
                  }
                });
              }
            });
          }
        });
      }

      return parentIds;
    },
    [releases]
  );

  // ✅ NEW: Build parent lookup map ONCE for better performance
  const buildParentLookupMap = useCallback((): Map<string, string[]> => {
    const parentMap = new Map<string, string[]>();

    if (!releases?.releaseScheduled) return parentMap;

    const addToMap = (childId: string, parentId: string) => {
      if (!parentMap.has(childId)) {
        parentMap.set(childId, []);
      }
      parentMap.get(childId)!.push(parentId);
    };

    // Build map from releaseScheduled data
    releases.releaseScheduled.forEach((releaseType: any) => {
      releaseType.projects?.forEach((quarterProject: any) => {
        quarterProject.project?.forEach((project: any) => {
          const compositeKey = project.releasePhaseId
            ? `${project.issueId}-${project.releasePhaseId}-${project.showOnTimeline}`
            : project.issueId;

          // Map children to parent
          project.childRelationships?.forEach((child: any) => {
            addToMap(child.issueId, compositeKey);

            // Map grandchildren to both parent and child
            child.childRelationships?.forEach((grandChild: any) => {
              addToMap(grandChild.issueId, compositeKey);
              const childCompositeKey = child.releasePhaseId
                ? `${child.issueId}-${child.releasePhaseId}-${child.showOnTimeline}`
                : child.issueId;
              addToMap(grandChild.issueId, childCompositeKey);
            });
          });

          // Map features to parent
          project.features?.forEach((feature: any) => {
            const featureUniqueId = feature.featureId || feature.issueId;
            addToMap(featureUniqueId, compositeKey);
          });
        });
      });
    });

    // Build map from tbd data
    if (releases.tbd && Array.isArray(releases.tbd)) {
      releases.tbd.forEach((tbdSection: any) => {
        tbdSection.project?.forEach((tbdItem: any) => {
          const compositeKey = tbdItem.releasePhaseId
            ? `${tbdItem.issueId}-${tbdItem.releasePhaseId}-${tbdItem.showOnTimeline}`
            : tbdItem.issueId;

          tbdItem.childRelationships?.forEach((child: any) => {
            addToMap(child.issueId, compositeKey);
          });

          tbdItem.features?.forEach((feature: any) => {
            const featureUniqueId = feature.featureId || feature.issueId;
            addToMap(featureUniqueId, compositeKey);
          });
        });
      });
    }

    return parentMap;
  }, [releases]);

  const updateHighlighting = useCallback(
    (results: SearchResult[], selectionOverride?: Set<string>) => {
      const activeSelections = selectionOverride ?? selectedResults;

      // ✅ Build parent lookup map ONCE for all results (performance optimization)
      const parentLookupMap = buildParentLookupMap();

      // If we have selected results, only highlight those
      if (activeSelections.size > 0) {
        // Map composite keys back to actual result IDs (handles IDs that include '-')
        const selectedFeatureIds = Array.from(activeSelections).map(
          selectedKey => {
            const match = results.find(r => {
              const k = r.releasePhaseId
                ? `${r.id}-${r.parentId || 'noparent'}-${r.releasePhaseId}-${
                    r.showOnTimeline
                  }`
                : r.parentId
                ? `${r.id}-${r.parentId}`
                : r.id;
              return k === selectedKey;
            });
            return match ? match.id : selectedKey;
          }
        );
        const uniqueSelectedIds = Array.from(new Set(selectedFeatureIds));
        const parentIssueIds = new Set<string>();

        // ✅ ONLY add parent IDs for results that are CHILD items
        // This prevents unnesting parent cards when parent matches but children don't
        // Check if any selected results are child items and find all their parents
        results.forEach(result => {
          const resultKey = result.releasePhaseId
            ? `${result.id}-${result.parentId || 'noparent'}-${
                result.releasePhaseId
              }-${result.showOnTimeline}`
            : result.parentId
            ? `${result.id}-${result.parentId}`
            : result.id;
          if (activeSelections.has(resultKey)) {
            // Only add parent IDs if this is a child/feature item
            if (result.type === 'child' && result.parentId) {
              parentIssueIds.add(result.parentId);

              // ✅ OPTIMIZED: Use lookup map for O(1) access instead of O(n) search
              const allParents = parentLookupMap.get(result.id) || [];
              allParents.forEach(parentId => parentIssueIds.add(parentId));
            } else if (result.type !== 'child') {
            }
          }
        });

        // Combine selected IDs and parent IDs for highlighting
        const allIdsToHighlight = [
          ...uniqueSelectedIds,
          ...Array.from(parentIssueIds)
        ];

        dispatch(
          actions.updateHighlightedIssue({
            relatedIssues: allIdsToHighlight,
            unnestParentIds: Array.from(parentIssueIds)
          })
        );

        if (parentIssueIds.size > 0) {
          // Save the previous state (it's safe to save multiple times)
          dispatch(actions.savePreviousUnNestAllState());
          // Don't set global unNestAll - rely on selective expansion via unnestParentIds
        }
      } else {
        // No selections, highlight all search results
        const matchingIssueIds = new Set<string>();
        const parentIssueIds = new Set<string>();

        // ✅ ONLY add parent IDs for child/feature items
        results.forEach(result => {
          matchingIssueIds.add(result.id);
          // Only add to unnest list if this is a child that matched
          if (result.type === 'child' && result.parentId) {
            parentIssueIds.add(result.parentId);

            // Also find all grandparent IDs using the comprehensive search
            const allParents = findAllParentIds(result.id);
            allParents.forEach(parentId => parentIssueIds.add(parentId));
          } else if (result.type !== 'child') {
          }
        });

        // Combine both matching IDs and parent IDs for highlighting
        const allIdsToHighlight = [
          ...Array.from(matchingIssueIds),
          ...Array.from(parentIssueIds)
        ];

        dispatch(
          actions.updateHighlightedIssue({
            relatedIssues: allIdsToHighlight,
            unnestParentIds: Array.from(parentIssueIds)
          })
        );

        if (parentIssueIds.size > 0) {
          // Save the previous state (it's safe to save multiple times)
          dispatch(actions.savePreviousUnNestAllState());
          // Don't set global unNestAll - rely on selective expansion via unnestParentIds
        }
      }
    },
    [selectedResults, dispatch, actions, buildParentLookupMap, findAllParentIds]
  );

  useRefreshSearchOnViewChange({
    timelineViewMode,
    searchIndex,
    searchValue,
    generateSearchResults,
    searchFilterActive,
    selectedResults,
    setSearchResults,
    setLastSearchResults,
    setSelectedResults,
    updateHighlighting,
    dispatch,
    actions,
    searchTimeoutRef
  });

  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value);

      // Clear any pending search timeout to prevent multiple searches from running
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }

      if (value.length === 0) {
        setSearchResults([]);
        setShowDropdown(false);
        setLastSearchResults([]);
        setSelectedResults(new Set());
        setDropdownHiddenByEnter(false);

        // Clear filtering and highlighting if search is cleared
        dispatch(actions.clearSearchFilter());
        dispatch(actions.clearHighlightedIssues());

        return;
      }

      if (value.length < 3) {
        setSearchResults([]);
        setShowDropdown(false);
        setLastSearchResults([]);
        // Clear highlighting when search term is too short
        dispatch(actions.clearHighlightedIssues());
        return;
      }

      // Debounce search execution - wait 150ms after user stops typing
      // This dramatically reduces the number of search operations
      searchTimeoutRef.current = setTimeout(() => {
        // Clear previous highlights before applying new ones
        dispatch(actions.clearHighlightedIssues());

        const results = generateSearchResults(value);

        setSearchResults(results);
        setLastSearchResults(results);
        setShowDropdown(true);
        setDropdownHiddenByEnter(false);

        // ✅ Update highlighting to show EXACTLY what matches the search
        // This provides immediate visual feedback showing the same items as search suggestions
        if (results.length > 0) {
          console.log(
            '🔍 [handleSearch] Applying highlights for search results'
          );
          updateHighlighting(results);
        } else {
          // Clear highlighting if no results found
          dispatch(actions.clearHighlightedIssues());
        }
      }, 150);
    },
    [dispatch, actions, generateSearchResults, updateHighlighting]
  );

  const handleResultSelect = useCallback(
    (result: SearchResult) => {
      // ✅ FIX: Auto-open "In the Works" section if the clicked result is in TBD
      if (result.isInTheWorks) {
        dispatch(actions.setParkingLot(true));
      }

      // Toggle selection using composite key (id + parentId + phase) for unique tracking
      const compositeKey = result.releasePhaseId
        ? `${result.id}-${result.parentId || 'noparent'}-${
            result.releasePhaseId
          }-${result.showOnTimeline}`
        : result.parentId
        ? `${result.id}-${result.parentId}`
        : result.id;

      const newSelectedResults = new Set(selectedResults);
      if (newSelectedResults.has(compositeKey)) {
        newSelectedResults.delete(compositeKey);
      } else {
        newSelectedResults.add(compositeKey);
      }

      setSelectedResults(newSelectedResults);

      // Save the previous state BEFORE any changes (do this on every click)
      dispatch(actions.savePreviousUnNestAllState());

      // Immediately apply search filtering based on selections (but keep dropdown open)
      if (newSelectedResults.size > 0) {
        // Map selected results to their IDs or composite keys for filtering
        // ✅ FIX: Store composite keys for phased releases to enable phase-specific filtering
        // This ensures selecting "Public Beta" doesn't show "GA" instances
        const selectedIds = Array.from(newSelectedResults).map(selectedKey => {
          const match =
            searchResults.find(r => {
              const k = r.releasePhaseId
                ? `${r.id}-${r.parentId || 'noparent'}-${r.releasePhaseId}-${
                    r.showOnTimeline
                  }`
                : r.parentId
                ? `${r.id}-${r.parentId}`
                : r.id;
              return k === selectedKey;
            }) ||
            lastSearchResults.find(r => {
              const k = r.releasePhaseId
                ? `${r.id}-${r.parentId || 'noparent'}-${r.releasePhaseId}-${
                    r.showOnTimeline
                  }`
                : r.parentId
                ? `${r.id}-${r.parentId}`
                : r.id;
              return k === selectedKey;
            });

          if (!match) return selectedKey;

          // For main cards with phases, return a composite key for phase-specific filtering
          // Format: ${id}-${releasePhaseId} (phase-only, matches both timeline and roadmap cards)
          if (match.type !== 'child' && match.releasePhaseId) {
            // ✅ FIX: Use composite key WITHOUT showOnTimeline
            // This ensures BOTH mirror cards (timeline + roadmap) are displayed
            const compositeKey = `${match.id}-${match.releasePhaseId}`;
            return compositeKey;
          }

          // For features (child items) or non-phased items, use just the ID
          // This ensures ALL instances of the parent card with different showOnTimeline are shown
          return match.id;
        });
        const parentIssueIds = new Set<string>();

        // Check ALL selected items (not just current searchResults) and find their parents
        // We need to iterate through ALL selections and look up each one
        Array.from(newSelectedResults).forEach(selectedKey => {
          // Find the actual result object from either searchResults or lastSearchResults
          const searchResult =
            searchResults.find(r => {
              const k = r.releasePhaseId
                ? `${r.id}-${r.parentId || 'noparent'}-${r.releasePhaseId}-${
                    r.showOnTimeline
                  }`
                : r.parentId
                ? `${r.id}-${r.parentId}`
                : r.id;
              return k === selectedKey;
            }) ||
            lastSearchResults.find(r => {
              const k = r.releasePhaseId
                ? `${r.id}-${r.parentId || 'noparent'}-${r.releasePhaseId}-${
                    r.showOnTimeline
                  }`
                : r.parentId
                ? `${r.id}-${r.parentId}`
                : r.id;
              return k === selectedKey;
            });

          if (searchResult) {
            // ✅ ONLY add parent IDs if the search result is a CHILD item
            // This prevents unnesting parent cards when the parent itself matches but children don't
            if (searchResult.type === 'child' && searchResult.parentId) {
              parentIssueIds.add(searchResult.parentId);

              // Also find all grandparent IDs using the comprehensive search
              // ⚠️ Only add plain IDs for non-phased releases
              // For phased releases, the composite key in selectedIds is sufficient
              if (!searchResult.releasePhaseId) {
                const allParents = findAllParentIds(searchResult.id);
                allParents.forEach(parentCompositeKey => {
                  // Extract just the issueId from composite keys like "ARM-12345-phase1-true"
                  // Split and take the first part as the actual issue ID
                  const plainIssueId =
                    parentCompositeKey.split('-')[0] +
                    '-' +
                    parentCompositeKey.split('-')[1];
                  parentIssueIds.add(plainIssueId);
                });
              }
            } else if (searchResult.type !== 'child') {
              // Parent card itself matches - don't add to unnestParentIds
              // We only want to unnest parents whose CHILDREN match the search
            }
          }
        });

        // Apply search filter with composite keys for phased items
        const uniqueSelectedIds = Array.from(new Set(selectedIds));

        // ✅ FIX: Check if ANY selection is a phased item (main card with releasePhaseId)
        // We need to know this to decide whether to include parent IDs
        const hasPhasedSelections = Array.from(newSelectedResults).some(
          selectedKey => {
            const match =
              searchResults.find(r => {
                const k = r.releasePhaseId
                  ? `${r.id}-${r.parentId || 'noparent'}-${r.releasePhaseId}-${
                      r.showOnTimeline
                    }`
                  : r.parentId
                  ? `${r.id}-${r.parentId}`
                  : r.id;
                return k === selectedKey;
              }) ||
              lastSearchResults.find(r => {
                const k = r.releasePhaseId
                  ? `${r.id}-${r.parentId || 'noparent'}-${r.releasePhaseId}-${
                      r.showOnTimeline
                    }`
                  : r.parentId
                  ? `${r.id}-${r.parentId}`
                  : r.id;
                return k === selectedKey;
              });
            return match && match.type !== 'child' && match.releasePhaseId;
          }
        );

        // ✅ FIX: For phased releases, DON'T add parent IDs - use ONLY composite keys
        // This ensures phase-specific filtering (GA doesn't show Public Beta)
        // Only add parent IDs for child/feature relationships (non-phased)
        const itemsToFilter = hasPhasedSelections
          ? [...uniqueSelectedIds] // Phased items: composite keys only
          : [...uniqueSelectedIds, ...Array.from(parentIssueIds)]; // Child features: include parent IDs

        // ✅ Build composite keys for "In the Works" items with releasePhase data
        // ParkingLot checks for these composite keys when highlighting
        const compositeKeysForHighlighting: string[] = [];
        Array.from(newSelectedResults).forEach(selectedKey => {
          const searchResult =
            searchResults.find(r => {
              const k = r.releasePhaseId
                ? `${r.id}-${r.parentId || 'noparent'}-${r.releasePhaseId}-${
                    r.showOnTimeline
                  }`
                : r.parentId
                ? `${r.id}-${r.parentId}`
                : r.id;
              return k === selectedKey;
            }) ||
            lastSearchResults.find(r => {
              const k = r.releasePhaseId
                ? `${r.id}-${r.parentId || 'noparent'}-${r.releasePhaseId}-${
                    r.showOnTimeline
                  }`
                : r.parentId
                ? `${r.id}-${r.parentId}`
                : r.id;
              return k === selectedKey;
            });

          if (searchResult && searchResult.isInTheWorks) {
            // For "In the Works" items, add composite keys that ParkingLot expects
            if (searchResult.releasePhaseId) {
              const key = `${searchResult.id}-${searchResult.releasePhaseId}-false`;
              compositeKeysForHighlighting.push(key);
            }
            if (searchResult.releasePhase) {
              const key = `${searchResult.id}-${searchResult.releasePhase}-false`;
              compositeKeysForHighlighting.push(key);
            }
          }
        });

        // Build highlight keys with full id-phaseId-showOnTimeline format for TimelineCard
        const highlightKeysFromSelections: string[] = [];
        Array.from(newSelectedResults).forEach(selectedKey => {
          const searchResult = findSearchResultBySelectionKey(
            selectedKey,
            searchResults,
            lastSearchResults
          );
          if (searchResult) {
            highlightKeysFromSelections.push(
              ...buildHighlightKeysForResult(searchResult)
            );
          }
        });

        // For phased items: DON'T add parent IDs (would match all phases)
        // For child features: DO add parent IDs (so parent card shows)
        const allIdsToHighlight = [
          ...highlightKeysFromSelections,
          ...(hasPhasedSelections ? [] : Array.from(parentIssueIds)),
          ...compositeKeysForHighlighting
        ].filter((id): id is string => Boolean(id));

        // ⭐ AUTO-OPEN "IN THE WORKS" if ANY selected items are in TBD
        const hasMatchInTBD = Array.from(newSelectedResults).some(
          selectedKey => {
            const searchResult =
              searchResults.find(r => {
                const k = r.releasePhaseId
                  ? `${r.id}-${r.parentId || 'noparent'}-${r.releasePhaseId}-${
                      r.showOnTimeline
                    }`
                  : r.parentId
                  ? `${r.id}-${r.parentId}`
                  : r.id;
                return k === selectedKey;
              }) ||
              lastSearchResults.find(r => {
                const k = r.releasePhaseId
                  ? `${r.id}-${r.parentId || 'noparent'}-${r.releasePhaseId}-${
                      r.showOnTimeline
                    }`
                  : r.parentId
                  ? `${r.id}-${r.parentId}`
                  : r.id;
                return k === selectedKey;
              });
            return searchResult?.isInTheWorks === true;
          }
        );

        // Must Wins view: if any selected result is outside Must Wins, confirm before switching to Roadmap
        const selectedResultList = Array.from(newSelectedResults)
          .map(selectedKey => {
            const r = searchResults.find(rez => {
              const k = rez.releasePhaseId
                ? `${rez.id}-${rez.parentId || 'noparent'}-${
                    rez.releasePhaseId
                  }-${rez.showOnTimeline}`
                : rez.parentId
                ? `${rez.id}-${rez.parentId}`
                : rez.id;
              return k === selectedKey;
            });
            return (
              r ||
              lastSearchResults.find(rez => {
                const k = rez.releasePhaseId
                  ? `${rez.id}-${rez.parentId || 'noparent'}-${
                      rez.releasePhaseId
                    }-${rez.showOnTimeline}`
                  : rez.parentId
                  ? `${rez.id}-${rez.parentId}`
                  : rez.id;
                return k === selectedKey;
              })
            );
          })
          .filter((r): r is SearchResult => r != null);

        // Only show "switch to Roadmap?" dialog when result is outside Must Wins AND not In the Works (parking lot)
        const outsideMustWins = selectedResultList.filter(
          r => !isResultInMustWins(r, mustWinItemIds)
        );
        const outsideAndNotInTheWorks = outsideMustWins.filter(
          r => !r.isInTheWorks
        );
        if (
          mustWinViewActive &&
          mustWinItemIds.size > 0 &&
          outsideAndNotInTheWorks.length > 0
        ) {
          pendingSearchApplyRef.current = () => {
            dispatch(actions.clearHighlightedIssues());
            dispatch(
              actions.updateHighlightedIssue({
                relatedIssues: allIdsToHighlight,
                unnestParentIds: Array.from(parentIssueIds),
                source: 'search'
              })
            );
            if (hasMatchInTBD) dispatch(actions.setParkingLot(true));
            dispatch(actions.setSearchFilterActive(true));
            dispatch(actions.setSearchFilteredItems(itemsToFilter));
          };
          const outsideKeys = new Set(
            outsideAndNotInTheWorks.map(r =>
              r.releasePhaseId
                ? `${r.id}-${r.parentId || 'noparent'}-${r.releasePhaseId}-${
                    r.showOnTimeline
                  }`
                : r.parentId
                ? `${r.id}-${r.parentId}`
                : r.id
            )
          );
          pendingCancelRemoveKeysRef.current = outsideKeys;
          setSearchOutsideDialogOpen(true);
          return;
        }

        // IMPORTANT: Clear previous highlights first, then apply new ones
        dispatch(actions.clearHighlightedIssues());
        dispatch(
          actions.updateHighlightedIssue({
            relatedIssues: allIdsToHighlight,
            unnestParentIds: Array.from(parentIssueIds),
            source: 'search' // ✅ Mark as search highlight (clicking suggestion)
          })
        );
        if (hasMatchInTBD) {
          dispatch(actions.setParkingLot(true));
        }
        dispatch(actions.setSearchFilterActive(true));
        dispatch(actions.setSearchFilteredItems(itemsToFilter));
      } else {
        // No results selected, clear search filter and show all search matches

        dispatch(actions.clearSearchFilter());

        // Clear previous highlights first

        dispatch(actions.clearHighlightedIssues());

        const allResultIds = searchResults.map(r => r.id);
        const parentIssueIds = new Set<string>();

        // ✅ ONLY add parent IDs for results that are CHILD items
        // This prevents unnesting parent cards when parent matches but children don't
        searchResults.forEach(searchResult => {
          if (searchResult.type === 'child' && searchResult.parentId) {
            console.log(
              '🔵 [handleResultSelect - deselect all] Found child match, adding parent:',
              {
                childId: searchResult.id,
                parentId: searchResult.parentId
              }
            );
            parentIssueIds.add(searchResult.parentId);

            // Also find all grandparent IDs using the comprehensive search
            const allParents = findAllParentIds(searchResult.id);
            allParents.forEach(parentCompositeKey => {
              // Extract just the issueId from composite keys like "ARM-12345-phase1-true"
              // Split and take the first part as the actual issue ID
              const plainIssueId =
                parentCompositeKey.split('-')[0] +
                '-' +
                parentCompositeKey.split('-')[1];
              parentIssueIds.add(plainIssueId);
            });
          } else if (searchResult.type !== 'child') {
            console.log(
              '🔵 [handleResultSelect - deselect all] Parent card match (not unnesting):',
              {
                parentId: searchResult.id
              }
            );
          }
        });

        // Combine result IDs and parent IDs for highlighting
        const allIdsToHighlight = [
          ...allResultIds,
          ...Array.from(parentIssueIds)
        ];

        // ✅ DO NOT set unNestAll=true globally - parent cards should check unnestParentIds array

        dispatch(
          actions.updateHighlightedIssue({
            relatedIssues: allIdsToHighlight,
            unnestParentIds: Array.from(parentIssueIds)
          })
        );

        // ⭐ AUTO-OPEN "IN THE WORKS" if any search results are in TBD (Enter key pressed)
        // ✅ FIX: Check the isInTheWorks flag directly from search results
        const hasMatchInTBD = searchResults.some(
          result => result.isInTheWorks === true
        );

        // Keep "In The Works" section open if ANY search result is in TBD
        if (hasMatchInTBD) {
          dispatch(actions.setParkingLot(true));
        }

        if (parentIssueIds.size > 0) {
          // Save the previous state and use selective expansion for search results with child matches
          dispatch(actions.savePreviousUnNestAllState());
          // Don't set global unNestAll - rely on selective expansion via unnestParentIds
        }
      }
    },
    [
      selectedResults,
      searchResults,
      dispatch,
      actions,
      findAllParentIds,
      releases,
      mustWinViewActive,
      mustWinItemIds
    ]
  );

  const handleClearFilters = useCallback(() => {
    setSelectedResults(new Set());

    // Show all search results again
    if (searchResults.length > 0) {
      const allResultIds = searchResults.map(r => r.id);
      const parentIssueIds = new Set<string>();

      searchResults.forEach(result => {
        if (result.type === 'child' && result.parentId) {
          parentIssueIds.add(result.parentId);
        }
        // Also find all parent IDs using the comprehensive search
        const allParents = findAllParentIds(result.id);
        allParents.forEach(parentId => parentIssueIds.add(parentId));
      });

      // ✅ Build composite keys for "In the Works" items with releasePhase data
      const compositeKeysForHighlighting: string[] = [];
      searchResults.forEach(result => {
        if (result.isInTheWorks) {
          if (result.releasePhaseId) {
            compositeKeysForHighlighting.push(
              `${result.id}-${result.releasePhaseId}-false`
            );
          }
          if (result.releasePhase) {
            compositeKeysForHighlighting.push(
              `${result.id}-${result.releasePhase}-false`
            );
          }
        }
      });

      const allIdsToHighlight = [
        ...allResultIds,
        ...Array.from(parentIssueIds),
        ...compositeKeysForHighlighting
      ];

      dispatch(
        actions.updateHighlightedIssue({
          relatedIssues: allIdsToHighlight,
          unnestParentIds: Array.from(parentIssueIds),
          source: 'search' // ✅ Mark as search highlight (Enter key pressed)
        })
      );

      if (parentIssueIds.size > 0) {
        dispatch(actions.savePreviousUnNestAllState());
        // Don't set global unNestAll - rely on selective expansion via unnestParentIds
      }
    }
  }, [searchResults, dispatch, actions, findAllParentIds]);

  // Handle Enter key press to hide dropdown
  const handleEnterPress = useCallback(() => {
    setShowDropdown(false);
    setDropdownHiddenByEnter(true);

    // Filtering is now handled immediately in handleResultSelect
    // If no items are selected, apply filter for all search results
    if (selectedResults.size === 0 && searchResults.length > 0) {
      const applyEnterSearch = () => {
        const itemsToFilter = searchResults.map(r => r.id);
        dispatch(actions.setSearchFilterActive(true));
        dispatch(actions.setSearchFilteredItems(itemsToFilter));
        const parentIssueIds = new Set<string>();

        // ✅ DEBUG: Count how many are actually children
        const childResults = searchResults.filter(r => r.type === 'child');
        const parentResults = searchResults.filter(r => r.type !== 'child');

        searchResults.forEach(result => {
          // Only process child/feature results to find their parents
          if (result.type === 'child') {
            // Add direct parent ID if available
            if (result.parentId) {
              console.log(
                '⌨️ [handleEnterPress] Feature matched - adding direct parent:',
                {
                  featureId: result.id,
                  featureTitle: result.title,
                  parentId: result.parentId
                }
              );
              parentIssueIds.add(result.parentId);
            }

            // Also find all parent IDs using comprehensive search (handles nested relationships)
            const allParents = findAllParentIds(result.id);
            console.log(
              '⌨️ [handleEnterPress] Finding all parents for feature:',
              {
                featureId: result.id,
                featureTitle: result.title,
                foundParents: allParents
              }
            );
            allParents.forEach(parentId => parentIssueIds.add(parentId));
          } else {
            // ✅ DEBUG: Log when we skip a parent card
            console.log(
              '⌨️ [handleEnterPress] Skipping parent card (not unnesting):',
              {
                id: result.id,
                title: result.title,
                type: result.type
              }
            );
          }
        });

        // ✅ Build composite keys for "In the Works" items with releasePhase data
        // ParkingLot checks for these composite keys when highlighting
        const compositeKeysForHighlighting: string[] = [];
        searchResults.forEach(result => {
          if (result.isInTheWorks) {
            // For "In the Works" items, add composite keys that ParkingLot expects
            if (result.releasePhaseId) {
              const key = `${result.id}-${result.releasePhaseId}-false`;
              compositeKeysForHighlighting.push(key);
            }
            if (result.releasePhase) {
              const key = `${result.id}-${result.releasePhase}-false`;
              compositeKeysForHighlighting.push(key);
            }
          }
        });

        // Combine items to filter, parent IDs, and composite keys for highlighting
        const allIdsToHighlight = [
          ...itemsToFilter,
          ...Array.from(parentIssueIds),
          ...compositeKeysForHighlighting // ✅ Include composite keys for "In the Works" items
        ];

        // ✅ DO NOT set unNestAll=true globally
        // Parent cards should ONLY unnest if their ID is in unnestParentIds array
        // Setting unNestAll=true would unnest ALL parents, not just matching ones

        dispatch(
          actions.updateHighlightedIssue({
            relatedIssues: allIdsToHighlight,
            unnestParentIds: Array.from(parentIssueIds),
            source: 'search' // ✅ Mark as search highlight (showing results without selections)
          })
        );

        if (parentIssueIds.size > 0) {
          dispatch(actions.savePreviousUnNestAllState());
        }
      };

      // Must Wins view: only confirm when result is outside Must Wins AND not In the Works (parking lot)
      if (
        mustWinViewActive &&
        mustWinItemIds.size > 0 &&
        searchResults.some(
          r => !isResultInMustWins(r, mustWinItemIds) && !r.isInTheWorks
        )
      ) {
        pendingSearchApplyRef.current = applyEnterSearch;
        pendingCancelRemoveKeysRef.current = null; // Enter path: no single "trigger" result to unselect
        setSearchOutsideDialogOpen(true);
        return;
      }
      applyEnterSearch();
    }
  }, [
    selectedResults,
    searchResults,
    dispatch,
    actions,
    findAllParentIds,
    mustWinViewActive,
    mustWinItemIds
  ]);

  // Handle input click to show dropdown again if there are existing results
  const handleInputClick = useCallback(() => {
    if (
      searchValue.length >= 3 &&
      lastSearchResults.length > 0 &&
      !showDropdown
    ) {
      setSearchResults(lastSearchResults);
      setShowDropdown(true);
      setDropdownHiddenByEnter(false);
    }
  }, [searchValue, lastSearchResults, showDropdown]);

  const handleClearSearch = useCallback(() => {
    dispatch(actions.clearSearchFilter());
    dispatch(actions.clearHighlightedIssues());
    setSelectedResults(new Set());
    // Remove issueId from URL so a refresh doesn't re-apply the filter
    clearViewStateFromUrl();
  }, [dispatch, actions]);

  const handleSearchOutsideConfirm = useCallback(() => {
    dispatch(actions.switchToRoadmapForSearch());
    pendingSearchApplyRef.current?.();
    pendingSearchApplyRef.current = null;
    setSearchOutsideDialogOpen(false);
  }, [dispatch, actions]);

  const handleSearchOutsideCancel = useCallback(() => {
    pendingSearchApplyRef.current = null;
    setSearchOutsideDialogOpen(false);
    // Unselect only the result(s) that triggered the dialog; keep other selections intact
    const keysToRemove = pendingCancelRemoveKeysRef.current;
    pendingCancelRemoveKeysRef.current = null;
    if (keysToRemove?.size) {
      setSelectedResults(prev => {
        const next = new Set(prev);
        keysToRemove.forEach(k => next.delete(k));
        return next;
      });
    }
    setShowDropdown(true);
    setDropdownHiddenByEnter(false);
  }, [dispatch, actions]);

  return (
    <div
      className="spectrum-Shell-leftContainer searchContainer"
      ref={containerRef}
      style={{ position: 'relative' }}
    >
      <DeleteConfirmationDialog
        isOpen={searchOutsideDialogOpen}
        title="Search result outside current view"
        message={SEARCH_OUTSIDE_MUST_WINS_MSG}
        confirmButtonText="OK"
        cancelButtonText="Cancel"
        onConfirm={handleSearchOutsideConfirm}
        onCancel={handleSearchOutsideCancel}
      />
      <SearchField
        placeholder="Search (min 3 characters)"
        aria-label="Search"
        width="size-3600"
        maxLength={100}
        value={searchValue}
        onChange={handleSearch}
        showDropdown={showDropdown}
        onResultSelect={handleResultSelect}
        searchResults={searchResults.map(result => {
          const resultKey = result.releasePhaseId
            ? `${result.id}-${result.parentId || 'noparent'}-${
                result.releasePhaseId
              }-${result.showOnTimeline}`
            : result.parentId
            ? `${result.id}-${result.parentId}`
            : result.id;
          return {
            ...result,
            selected: selectedResults.has(resultKey)
          };
        })}
        onEnterPress={handleEnterPress}
        onInputClick={handleInputClick}
        onClear={handleClearSearch}
        searchFilterActive={searchFilterActive}
        searchFilteredItemsCount={searchFilteredItems.length}
      />

      {/* Number Badge for Selected Results or Filtered Items */}
      {(selectedResults.size > 0 ||
        (searchFilterActive &&
          searchFilteredItems.length > 0 &&
          selectedResults.size === 0)) && (
        <div className={styles.searchBadge}>
          {(() => {
            const badgeValue =
              selectedResults.size > 0
                ? selectedResults.size
                : searchFilteredItems.length;

            return badgeValue;
          })()}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
